import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useFetchUnitTestsAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { Alert, Card, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'

const UnitTestsHistogram = ({ token, showOnlyCurrentStudents = false }) => {
    const { data: analytics, isFetching, error } = useFetchUnitTestsAnalyticsQuery({ token })

    const data = analytics && [
        ...analytics.current.map(unitTestAnalyticsItem => ({
            testName: unitTestAnalyticsItem.testName,
            percentOfStudentsPassedTheTest: unitTestAnalyticsItem.percentOfStudentsPassedTheTest,
            numberOfStudentsPassedTheTest: unitTestAnalyticsItem.numberOfStudentsPassedTheTest,
            name: showOnlyCurrentStudents ? 'Students' : 'My students',
            fill: '#1677ff', // Primary blue color for My students
        })),
        ...(showOnlyCurrentStudents
            ? []
            : analytics.all.map(unitTestAnalyticsItem => ({
                  testName: unitTestAnalyticsItem.testName,
                  percentOfStudentsPassedTheTest: unitTestAnalyticsItem.percentOfStudentsPassedTheTest,
                  numberOfStudentsPassedTheTest: unitTestAnalyticsItem.numberOfStudentsPassedTheTest,
                  name: 'All students',
                  fill: '#52c41a', // Green color for All students
              }))),
    ]

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const testName = payload[0]?.payload?.testName;
            const entries = payload.filter(entry => entry.value !== undefined && entry.value !== null);
            
            if (!entries.length || !testName) return null;
            
            return (
                <Card size='small'>
                    <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                        {`Passed ${testName}`}
                    </Title>
                    {entries.map((entry, index) => {
                        // Find original data with number of students who passed
                        const originalData = data.find(item => 
                            item.testName === testName && 
                            item.name === entry.dataKey
                        );
                        
                        return (
                            <Text key={index} style={{ display: 'block' }}>
                                {`${entry.name}: ${entry.value}% - ${originalData?.numberOfStudentsPassedTheTest || 0} students`}
                            </Text>
                        );
                    })}
                </Card>
            );
        }
        return null;
    };

    // Custom label renderer to handle label placement and color
    const renderCustomBarLabel = props => {
        const { x, y, width, value } = props
        return value > 6 ? (
            <text x={x + width - 5} y={y + 15} fill="#fff" textAnchor="end" dominantBaseline="middle">
                {`${value}%`}
            </text>
        ) : (
            <text x={x + width + 5} y={y + 15} fill="#000" textAnchor="start" dominantBaseline="middle">
                {`${value}%`}
            </text>
        )
    }

    // Generate a detailed description of the data for accessibility
    const generateDescription = () => {
        if (!data) return 'Loading data...'
        let description = ''
        if (analytics?.numberOfCurrentStudents)
            description += `Total number of ${showOnlyCurrentStudents ? '' : 'my'} students: ${
                analytics.numberOfCurrentStudents
            } students. `
        if (analytics?.numberOfAllStudents)
            description += `Total number of all students: ${analytics.numberOfAllStudents} students. `
        description += 'Detailed breakdown of unit test results. '
        data.forEach(item => {
            description += `The ${item.testName} test was passed by ${item.numberOfStudentsPassedTheTest} of ${item.name}, making up ${item.percentOfStudentsPassedTheTest}% of the total. `
        })
        return description
    }

    if (error) return <Alert message={error} type='error' showIcon />

    // Group data by test name and create series for each student group
    const chartData = data && analytics ? 
        Array.from(new Set(data.map(item => item.testName))).map(testName => {
            const result = { testName };
            const myStudentsItem = data.find(item => item.testName === testName && (item.name === 'My students' || item.name === 'Students'));
            const allStudentsItem = data.find(item => item.testName === testName && item.name === 'All students');
            
            if (myStudentsItem) {
                result[myStudentsItem.name] = myStudentsItem.percentOfStudentsPassedTheTest;
                result[`${myStudentsItem.name}-count`] = myStudentsItem.numberOfStudentsPassedTheTest;
            }
            
            if (allStudentsItem) {
                result['All students'] = allStudentsItem.percentOfStudentsPassedTheTest;
                result[`All students-count`] = allStudentsItem.numberOfStudentsPassedTheTest;
            }
            
            return result;
        }).sort((a, b) => {
            // Sort by the primary student group value
            const aValue = showOnlyCurrentStudents 
                ? (a['Students'] || 0) 
                : (a['My students'] || 0);
            const bValue = showOnlyCurrentStudents 
                ? (b['Students'] || 0) 
                : (b['My students'] || 0);
            return bValue - aValue; // Sort descending
        }) : [];

    return (
        <Spin spinning={isFetching} tip='Loading unit tests analytics...'>
            <div
                role='img'
                aria-label='Bar chart showing the percent of students who passed each unit test'
                aria-describedby={`unitTestsChartDescription-${token}`}
            >
                <ResponsiveContainer width="100%" height={Math.max(480, chartData.length * 40)}>
                    <BarChart 
                        data={chartData} 
                        layout="vertical"
                        margin={{ top: 20, right: 50, left: 20, bottom: 40 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis 
                            type="number"
                            tickFormatter={value => `${value}%`}
                            domain={[0, 100]}
                            label={{ 
                                value: 'Percent of Students Who Passed', 
                                position: 'bottom', 
                                offset: 0,
                                dy: 20
                            }}
                        />
                        <YAxis 
                            dataKey="testName"
                            type="category"
                            width={150}
                            label={{ 
                                value: 'Test Name', 
                                angle: -90, 
                                position: 'insideLeft',
                                dx: -30
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {!showOnlyCurrentStudents && (
                            <Bar 
                                dataKey="All students" 
                                fill="#52c41a" 
                                radius={[0, 4, 4, 0]}
                                name="All students"
                                label={renderCustomBarLabel}
                            />
                        )}
                        <Bar 
                            dataKey={showOnlyCurrentStudents ? "Students" : "My students"} 
                            fill="#1677ff" 
                            radius={[0, 4, 4, 0]} 
                            name={showOnlyCurrentStudents ? "Students" : "My students"}
                            label={renderCustomBarLabel}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div id={`unitTestsChartDescription-${token}`} className='visually-hidden focusable' tabIndex='0'>
                    {generateDescription()}
                </div>
            </div>
        </Spin>
    )
}

export default UnitTestsHistogram
